import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateTemplateFromReviewProcessDocument,
  CreateTemplateFromReviewProcessMutationVariables,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export const SaveAsTemplateModal: React.FC<{
  reviewId: number
  reviewName: string | null | undefined
  onClose?: () => void
}> = ({ reviewId, reviewName, onClose }) => {
  const { access } = useCurrentSession()

  const [createTemplateFromReviewProcess, { loading: creatingTemplate }] = useMutation(
    CreateTemplateFromReviewProcessDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.createFromReviewProcessPerformanceReviewProcessCustomTemplate,
    }
  )

  const form = useForm<CreateTemplateFromReviewProcessMutationVariables>({
    defaultValues: {
      authorId: access.id,
      reviewProcessId: reviewId,
      name: reviewName || '',
      templateDescription: '',
    },
    validate: (data) => {
      const errors: FormErrors<CreateTemplateFromReviewProcessMutationVariables> = {}
      if (!data.name) {
        errors.name = i18n.t('performance_process.reviews.templates.save_as_template.error_message')
      }

      return errors
    },
    onSubmit: async (data) => {
      const response = await createTemplateFromReviewProcess({
        variables: {
          authorId: data.authorId,
          reviewProcessId: reviewId,
          name: data.name,
          templateDescription: data.templateDescription,
        },
      })

      const result = handleResult(response, (formErrors) => {
        form.setErrors({ name: formErrors.name })
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'performance_process.reviews.templates.save_as_template.confirmation_message'
          ),
        })

        onClose?.()
      }

      return result
    },
  })

  return (
    <Modal
      title={i18n.t('performance_process.reviews.templates.save_as_template.title')}
      description={i18n.t('performance_process.reviews.templates.save_as_template.description', {
        name: reviewName || '',
      })}
      illustration="window-user"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={creatingTemplate}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={creatingTemplate || form.isSubmitting || !form.values.name}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s32">
        <Box gap="s16">
          <TextField
            mandatory
            label={i18n.t('common.name')}
            placeholder={i18n.t(
              'performance_process.reviews.templates.save_as_template.name_placeholder'
            )}
            value={form.values.name || undefined}
            onChange={(value) => form.setFieldValue('name', value)}
            readonly={creatingTemplate}
            error={form.errors.name}
          />
          <TextArea
            label={i18n.t('common.description')}
            placeholder={i18n.t(
              'performance_process.reviews.templates.save_as_template.template_description_placeholder'
            )}
            value={form.values.templateDescription || ''}
            onChange={(value) => form.setFieldValue('templateDescription', value)}
            readonly={creatingTemplate}
          />
        </Box>
      </Box>
    </Modal>
  )
}
